/*
 * Copyright '2024' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { Toggle } from "si-mfes-library/toggles/toggle";
import { ITogglesProvider } from "si-mfes-library/toggles/toggles-provider.interface";
import { TogglesService } from "si-mfes-library/toggles/toggles.service";

export class TogglesProvider implements ITogglesProvider {
  public static readonly GLOBAL_TOGGLES_SERVICE_KEY = "togglesService";

  togglesService: any;
  cachedToggles: Toggle[];
  cachePromise: Promise<void> | null = null;

  constructor(window: Window) {
    this.togglesService = (window[TogglesProvider.GLOBAL_TOGGLES_SERVICE_KEY] ||
      new TogglesService()) as ITogglesProvider;
  }

  public async getAll(): Promise<Toggle[]> {
    await this.cacheToggles();
    return this.cachedToggles;
  }

  public async get(toggleName: string): Promise<Toggle> {
    return await this.findToggle(toggleName);
  }

  public async isActive(toggleName: string): Promise<boolean> {
    const toggle = await this.findToggle(toggleName);
    return toggle.active;
  }

  private async findToggle(toggleName: string): Promise<Toggle> {
    await this.cacheToggles();

    let toggle = this.cachedToggles.find(
      (toggle) => toggle.name === toggleName
    );

    if (!toggle) {
      toggle = (await this.togglesService.get(toggleName)) as Toggle;
      if (!toggle) {
        toggle = {
          name: toggleName,
          active: false,
        };
      } else {
        this.cachedToggles.push(toggle);
      }
    }

    return toggle;
  }

  private async cacheToggles(): Promise<void> {
    if (this.cachedToggles?.length > 0) {
      return;
    }

    if (!this.cachePromise) {
      this.cachePromise = this.togglesService
        .getAll()
        .then((toggles: Toggle[]) => {
          this.cachedToggles = toggles ?? [];
          this.cachePromise = null;
        });
    }

    await this.cachePromise;
  }
}
